import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Container, Card, Button, Alert, Spinner, Badge, Row, Col } from "react-bootstrap";
import { fetchOrder, deleteOrder } from "../../Service/Order.service";
import "./OrderDetail.css";
import Header from '../../components/HeaderTop/HeaderTop'
const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.user_role === 4;

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const data = await fetchOrder(id);
        setOrder(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      loadOrder();
    }
  }, [id, isAdmin]);

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      setIsDeleting(true);
      try {
        await deleteOrder(id);
        navigate("/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e", { 
          state: { message: "Order deleted successfully" } 
        });
      } catch (err) {
        setError(err.message);
        setIsDeleting(false);
      }
    }
  };

  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <Container className="text-center mt-5 py-5">
        <Spinner animation="grow" variant="primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading order details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger" className="shadow-sm">
          <Alert.Heading>Error loading order</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="mt-5">
        <Alert variant="warning" className="shadow-sm">
          <Alert.Heading>Order Not Found</Alert.Heading>
          <p>The requested order could not be found.</p>
          <Button 
            variant="outline-warning" 
            onClick={() => navigate("/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e")}
          >
            Back to Orders
          </Button>
        </Alert>
      </Container>
    );
  }

  const statusVariant = {
    delivery: "success",
    pending: "warning",
    cancelled: "danger",
    processing: "info"
  }[order.order_status] || "secondary";

  return (
    <>
    <Header/>
     <div className="d-flex justify-content-end mt-4">
        <Button 
          variant="outline-secondary" 
          className="me-2 btn-hover-scale bg-primary text-white"
          onClick={() => navigate("/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e")}
        >
          <i className="bi bi-arrow-left me-2"></i>Back to Orders
        </Button>
      </div>
    <Container className="my-5 order-detail-container">
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-gradient">Order Details</h2>
          <p className="text-muted">Order ID: #{order.order_id}</p>
        </div>
        <div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              className="btn-hover-scale"
              onClick={() => navigate(`/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e/${id}/edit`)}
            >
              <i className="bi bi-pencil me-2"></i>Edit
            </Button>
            <Button 
              variant="outline-danger" 
              className="btn-hover-scale"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Deleting...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-2"></i>Delete
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-0 shadow-sm animate-fade-in">
        <Card.Header className="bg-primary text-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-receipt me-2"></i>
              Order Summary
            </h5>
            <Badge pill bg="light" text={statusVariant} className="fs-6 px-3 py-2">
              {order.order_status.toUpperCase()}
            </Badge>
          </div>
        </Card.Header>
        
        <Card.Body className="p-4">
          <Row className="g-4">
            {/* Customer Information */}
            <Col md={6}>
              <div className="info-section">
                <h5 className="section-title">
                  <i className="bi bi-person-circle me-2"></i>
                  Customer Information
                </h5>
                <div className="info-content">
                  <div className="info-item">
                    <span className="info-label">Company:</span>
                    <span className="info-value">{order.company_name || "N/A"}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Customer Name:</span>
                    <span className="info-value">{order.customer_name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">{order.phone}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{order.email || "N/A"}</span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Order Details */}
            <Col md={6}>
              <div className="info-section">
                <h5 className="section-title">
                  <i className="bi bi-cart3 me-2"></i>
                  Order Details
                </h5>
                <div className="info-content">
                  <div className="info-item">
                    <span className="info-label">Delivery Date:</span>
                    <span className="info-value">
                      {new Date(order.delivery_date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Red Injera:</span>
                    <span className="info-value">{order.red_injera_quantity}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">White Injera:</span>
                    <span className="info-value">{order.white_injera_quantity}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Packaging Type:</span>
                    <span className="info-value text-capitalize">{order.packaging_type}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Total Price:</span>
                    <span className="info-value fw-bold text-success">
                      ${parseFloat(order.total_price).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Delivery Information */}
            <Col md={6}>
              <div className="info-section">
                <h5 className="section-title">
                  <i className="bi bi-truck me-2"></i>
                  Delivery Information
                </h5>
                <div className="info-content">
                  <div className="info-item">
                    <span className="info-label">Location:</span>
                    <span className="info-value">{order.delivery_location || "N/A"}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Address:</span>
                    <span className="info-value">{order.address || "N/A"}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">City:</span>
                    <span className="info-value">{order.city || "N/A"}</span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Special Instructions */}
            {order.special_instructions && (
              <Col md={6}>
                <div className="info-section">
                  <h5 className="section-title">
                    <i className="bi bi-chat-square-text me-2"></i>
                    Special Instructions
                  </h5>
                  <div className="info-content">
                    <div className="special-instructions">
                      {order.special_instructions}
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>

        <Card.Footer className="bg-light d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <i className="bi bi-clock-history me-1"></i>
            Last updated: {new Date(order.updated_at).toLocaleString()}
          </small>
          <small className="text-muted">
            Created by: {order.user_firstName} {order.user_lastName}
          </small>
        </Card.Footer>
      </Card>

     
    </Container>
    </>
  );
};

export default OrderDetail;